import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "ghost" | "danger" | "subtle";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-gold text-black font-bold hover:bg-gold2 active:translate-y-[1px] disabled:bg-dim disabled:text-muted",
  ghost:
    "bg-transparent text-text border border-border2 hover:border-gold hover:text-gold disabled:text-dim disabled:border-border",
  danger:
    "bg-danger text-white font-semibold hover:bg-[#d96a6a] disabled:bg-dim disabled:text-muted",
  subtle:
    "bg-surface2 text-text border border-border hover:bg-surface3 disabled:text-muted",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-xs rounded-md min-w-[64px]",
  md: "h-10 px-4 text-sm rounded-md min-w-[88px]",
  lg: "h-12 px-6 text-sm rounded-lg min-w-[120px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    block = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 transition-colors duration-150 gold-focus select-none",
        "disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});
